import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { PagedListRequest } from 'domain/types/global/pagedListRequest';
import { useTranslation } from 'react-i18next';
import { CampanhaPedidoResgateResponse } from 'domain/types/_carteira/campanhaPedidoResgate/campanhaPedidoResgateResponse';
import { RootStateCampanha } from '../../../../store/campanhaReducer';
import { GetSolicitadosByCampanha, ResetSolicitadosByCampanha } from 'domain/store/_carteira/campanhaPedidoResgate/actions/campanhaPedidoResgateAction';
import moment from 'moment';
import 'moment/dist/locale/pt-br'

import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { CAMPANHA_PEDIDORESGATE_GET_BY_CAMPANHA } from 'domain/types/_carteira/campanhaPedidoResgate/campanhaPedidoResgate';
import { CampanhaParameters } from '../../../../infra/routes/campanhaRoutes';
import { UsuarioSistemaPerfilEnum } from 'domain/types/usuarioSistemaPerfil/usuarioSistemaPerfilEnum';
import { useCampanhaId } from '../../../../hooks/useCampanhaId';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Avatar,
  IconButton,
  SelectChangeEvent,
  Chip,
  Badge,
  Checkbox
} from '@mui/material';
import { Icon } from '@iconify/react';
import { formatToBRL } from 'base/src/utils/mathUtil';
import { Tag } from 'base/src/components/Tag';
import { Button } from 'base/src/components/Button';
import { Modal, ModalContent } from 'base/src/components/Modal';
import NoData from 'base/src/components/NoData';
import AprovarPedidoDetalhes from '../aprovarPedidoDetalhes/aprovarPedidoDetalhes';
import FilterDrawer from './components/filterDrawer/filterDrawer';
import { useTheme } from 'base/src/utils/context/themeContext';
import { AprovarResgateBulk, ReprovarResgateBulk } from '../../../../store/aprovarPedido/actions/aprovarPedidoAction';
import { CAMPANHA_RESGATE_APROVAR_BULK, CAMPANHA_RESGATE_REPROVAR_BULK } from '../../../../types/aprovarPedido/aprovarPedido';
import StateMensage from 'base/src/view/states/state';

const connector = connectUtil(
  (state: RootStateCampanha) => ({
    CampanhaPedidoResgate: state.CampanhaPedidoResgateReducer.CampanhaPedidoResgateByCampanha,
    CampanhasDisponiveis: state.CampanhasReducer?.campanhasAll,
    mudancaStatus: state.AprovarPedidoReducer.mudancaStatus,
    mudancaStatusBulk: state.AprovarPedidoReducer.mudancaStatusBulk,
    loading: state.BaseReducer.Loading,
    usuarioSistemaPerfil: state.UsuarioSistemaPerfilReducer.usuarioSistemaPerfil,
  }),
  { GetSolicitadosByCampanha, ResetSolicitadosByCampanha, AprovarResgateBulk, ReprovarResgateBulk }
);

export interface PagedResgatesProps extends PropsFromRedux<typeof connector> {
  take: number;
  campanhaId: number;
}

function PagedResgates(props: PagedResgatesProps) {
  const navigate = useNavigate();
  const campanhaId = useCampanhaId();
  const { theme } = useTheme();
  const [request, setRequest] = useState<PagedListRequest<CampanhaPedidoResgateResponse>>({
    skip: 0,
    take: props.take,
    search: '',
    filter: [],
    data: {} as CampanhaPedidoResgateResponse
  });

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]); // Start with no filter selected
  const [premiacaoTipoFilter, setPremiacaoTipoFilter] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CampanhaPedidoResgateResponse | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'aprovar' | 'reprovar' | null>(null);
  const [bulkMotivo, setBulkMotivo] = useState('');
  const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);
  const isSubmittingBulkRef = useRef<boolean>(false);

  const { t } = useTranslation();

  moment.locale("pt-br");

  // Status options
  const statusOptions = [
    { id: '1', nome: 'Solicitado' },
    { id: '2', nome: 'Aprovado' },
    { id: '3', nome: 'Não Aprovada' },
    { id: '4', nome: 'Pago/Entregue' },
    { id: '5', nome: 'Em Espera' }
  ];

  // Premiacao tipo options
  const premiacaoTipoOptions = [
    { id: '1', nome: 'Por Ranking' },
    { id: '4', nome: 'WebGold' }
  ];

  // Get status variant for Tag component
  function getStatusVariant(statusId: number) {
    if (statusId === 1 || statusId === 5) return 'warning';
    if (statusId === 2 || statusId === 4) return 'positive';
    if (statusId === 3 || statusId === 6) return 'danger';
    return 'positive';
  }

  // Get chip color for status tags
  function getStatusChipColor(statusId: number): 'default' | 'warning' | 'success' | 'error' {
    if (statusId === 1 || statusId === 5) return 'warning';
    if (statusId === 2 || statusId === 4) return 'success';
    if (statusId === 3 || statusId === 6) return 'error';
    return 'default';
  }

  // Handle pagination change
  function handlePageChange(_: unknown, newPage: number) {
    const newRequest = {
      ...request,
      skip: newPage * request.take
    };
    setRequest(newRequest);

    // Reset data to clear previous page data
    props.ResetSolicitadosByCampanha(newRequest, props.take);

    // Load new page data
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Handle rows per page change
  function handleRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newTake = parseInt(event.target.value, 10);
    const newRequest = {
      ...request,
      take: newTake,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous data
    props.ResetSolicitadosByCampanha(newRequest, newTake);

    // Load new data
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Handle search
  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    const searchVal = event.target.value;
    setSearchValue(searchVal);

    const newRequest = {
      ...request,
      search: searchVal,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous results
    props.ResetSolicitadosByCampanha(newRequest, props.take);

    // Load new filtered data
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  function handleStatusFilterChange(event: SelectChangeEvent<string[]>) {
    const values = event.target.value as string[];
    setStatusFilter(values);

    const filterArray = [];
    if (values.length > 0) {
      filterArray.push({
        Values: values,
        Id: 0,
        Name: "Status"
      });
    }
    if (premiacaoTipoFilter.length > 0) {
      filterArray.push({
        Values: premiacaoTipoFilter,
        Id: 1,
        Name: "PremiacaoTipo"
      });
    }

    const newRequest = {
      ...request,
      filter: filterArray,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosByCampanha(newRequest, props.take);

    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  function handlePremiacaoTipoFilterChange(event: SelectChangeEvent<string[]>) {
    const values = event.target.value as string[];
    setPremiacaoTipoFilter(values);

    const filterArray = [];
    if (statusFilter.length > 0) {
      filterArray.push({
        Values: statusFilter,
        Id: 0,
        Name: "Status"
      });
    }
    if (values.length > 0) {
      filterArray.push({
        Values: values,
        Id: 1,
        Name: "PremiacaoTipo"
      });
    }

    const newRequest = {
      ...request,
      filter: filterArray,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosByCampanha(newRequest, props.take);

    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Handle status chip toggle
  function handleStatusChipToggle(statusId: string) {
    const newStatusFilter = statusFilter.includes(statusId)
      ? statusFilter.filter(id => id !== statusId)
      : [...statusFilter, statusId];

    setStatusFilter(newStatusFilter);

    const filterArray = [];
    if (newStatusFilter.length > 0) {
      filterArray.push({
        Values: newStatusFilter,
        Id: 0,
        Name: "Status"
      });
    }
    if (premiacaoTipoFilter.length > 0) {
      filterArray.push({
        Values: premiacaoTipoFilter,
        Id: 1,
        Name: "PremiacaoTipo"
      });
    }

    const newRequest = {
      ...request,
      filter: filterArray,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosByCampanha(newRequest, props.take);
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Handle modal
  function handleOpenModal(item: CampanhaPedidoResgateResponse) {
    setSelectedItem(item);
    setIsModalOpen(true);
  }

  function handleCloseModal() {
    setTimeout(() => {
      setIsModalOpen(false);
      setSelectedItem(null);

      // Refresh data after modal closes to ensure we have the latest status
      props.ResetSolicitadosByCampanha(request, props.take);
      props.GetSolicitadosByCampanha([props.campanhaId], request, t);
    }, 50);
  }

  function handleOpenDrawer() {
    setIsDrawerOpen(true);
  }

  function handleCloseDrawer() {
    setIsDrawerOpen(false);
  }

  // Handle apply filters (close drawer after applying)
  function handleApplyFilters() {
    setIsDrawerOpen(false);
  }

  function handleDateStartChange(value: string) {
    setDateStart(value);

    const newRequest = {
      ...request,
      data: {
        ...request.data,
        DataInicio: value ? `${value}T00:00:00` : undefined,
        DataFim: dateEnd ? `${dateEnd}T23:59:59` : undefined
      },
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosByCampanha(newRequest, props.take);
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  function handleDateEndChange(value: string) {
    setDateEnd(value);

    const newRequest = {
      ...request,
      data: {
        ...request.data,
        DataInicio: dateStart ? `${dateStart}T00:00:00` : undefined,
        DataFim: value ? `${value}T23:59:59` : undefined
      },
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosByCampanha(newRequest, props.take);
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  function handleClearFilters() {
    setSearchValue('');
    setStatusFilter([]);
    setPremiacaoTipoFilter([]);
    setDateStart('');
    setDateEnd('');

    // Reset request to initial state
    const newRequest = {
      ...request,
      search: '',
      filter: [],
      data: {} as CampanhaPedidoResgateResponse,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data and load fresh data without filters
    props.ResetSolicitadosByCampanha(newRequest, props.take);
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  function handleNavigateToPagarPedido() {
    navigate(`/pagarPedido?${CampanhaParameters.campanhaId}=${campanhaId}`);
  }

  function handleNavigateToGerarLote() {
    navigate(`/gerarLote?${CampanhaParameters.campanhaId}=${campanhaId}`);
  }

  const isAdministradorCarteira = Boolean(
    props.usuarioSistemaPerfil?.find(u =>
      u.PerfilID === UsuarioSistemaPerfilEnum.AdministradorDeCarteira
    )
  );

  // Format pagination label
  function formatPaginationLabel({ from, to, count }: { from: number; to: number; count: number }) {
    return `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`;
  }

  // Map column keys to backend sort field paths
  const sortFieldMap: Record<string, string> = {
    'data': 'HoraPedido',
    'usuario': '_Usuario.NomeCompleto',
    'descricao': 'Premiacao.Descricao',
    'id': 'CampanhaPedidoResgateID',
    'tipo': 'Premiacao.PremiacaoTipo.PremiacaoTipoID',
    'chavepix': '_Usuario.ChavePix',
    'status': 'CampanhaPedidoResgateStatus.Titulo',
    'valor': 'ValorOriginal'
  };

  // Handle column sort (server-side)
  function handleSort(field: string) {
    const backendField = sortFieldMap[field];
    if (!backendField) return;

    const isAsc = sortField === field && sortOrder === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setSortOrder(newOrder);
    setSortField(field);

    const newRequest = {
      ...request,
      sort: `${backendField} ${newOrder}`,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosByCampanha(newRequest, props.take);
    props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Render sort icon
  function renderSortIcon(field: string) {
    if (sortField !== field) {
      return <Icon icon="mdi:unfold-more-horizontal" style={{ marginLeft: 4, fontSize: 16, opacity: 0.5 }} />;
    }
    return sortOrder === 'asc'
      ? <Icon icon="mdi:arrow-up" style={{ marginLeft: 4, fontSize: 16 }} />
      : <Icon icon="mdi:arrow-down" style={{ marginLeft: 4, fontSize: 16 }} />;
  }

  // Get data from state
  const data = props.CampanhaPedidoResgate?.Result || [];
  const totalCount = props.CampanhaPedidoResgate?.TotalCount || 0;
  const isLoading = props.loading.some(l => l.Name === CAMPANHA_PEDIDORESGATE_GET_BY_CAMPANHA);

  // Reset data when status changes and reload fresh data
  useEffect(() => {
    if (props.mudancaStatus && Object.keys(props.mudancaStatus).length > 0) {
      // Reset data first
      props.ResetSolicitadosByCampanha(request, props.take);

      // Then reload with fresh data
      props.GetSolicitadosByCampanha([props.campanhaId], request, t);
    }
  }, [props.mudancaStatus]);

  // Load initial data
  useEffect(() => {
    props.GetSolicitadosByCampanha([props.campanhaId], request, t);
  }, [props.campanhaId]);

  // Adjust skip if current page is out of range
  useEffect(() => {
    if (totalCount > 0 && !isLoading) {
      const calculatedPage = Math.floor(request.skip / request.take);
      const maxPage = Math.max(0, Math.ceil(totalCount / request.take) - 1);

      if (calculatedPage > maxPage) {
        const newSkip = maxPage * request.take;
        const newRequest = {
          ...request,
          skip: newSkip
        };
        setRequest(newRequest);
        props.GetSolicitadosByCampanha([props.campanhaId], newRequest, t);
      }
    }
  }, [totalCount, isLoading]);

  // Calculate current page and clamp to valid range
  const calculatedPage = Math.floor(request.skip / request.take);
  const maxPage = Math.max(0, Math.ceil(totalCount / request.take) - 1);
  const currentPage = Math.min(calculatedPage, maxPage);
  const activeFilterCount = statusFilter.length + premiacaoTipoFilter.length + (dateStart ? 1 : 0) + (dateEnd ? 1 : 0);

  // Bulk selection — only items in status 1 (Solicitado) are selectable for bulk approve/reject
  const selectableItems = data.filter(function isSelectable(item) { return item.CampanhaPedidoResgateStatusID === 1; });
  const selectableIds = selectableItems.map(function getId(item) { return item.CampanhaPedidoResgateID; });
  const allPageSelected = selectableIds.length > 0 && selectableIds.every(function isSel(id) { return selectedIds.includes(id); });
  const somePageSelected = selectableIds.some(function isSel(id) { return selectedIds.includes(id); }) && !allPageSelected;
  const isBulkLoading = props.loading.some(function isLoad(l) { return l.Name === CAMPANHA_RESGATE_APROVAR_BULK || l.Name === CAMPANHA_RESGATE_REPROVAR_BULK; });

  function handleToggleRow(id: number) {
    setSelectedIds(function toggle(prev) {
      return prev.includes(id) ? prev.filter(function notId(x) { return x !== id; }) : [...prev, id];
    });
  }

  function handleToggleAllPage() {
    if (allPageSelected) {
      setSelectedIds(function removePage(prev) {
        return prev.filter(function notInPage(id) { return !selectableIds.includes(id); });
      });
    } else {
      setSelectedIds(function addPage(prev) {
        const merged = new Set([...prev, ...selectableIds]);
        return Array.from(merged);
      });
    }
  }

  function handleClearSelection() {
    setSelectedIds([]);
  }

  function handleOpenBulkModal(action: 'aprovar' | 'reprovar') {
    setBulkAction(action);
    setBulkMotivo('');
    setIsBulkModalOpen(true);
  }

  function handleCloseBulkModal() {
    setIsBulkModalOpen(false);
    setBulkAction(null);
    setBulkMotivo('');
  }

  async function handleConfirmBulk() {
    if (isSubmittingBulkRef.current) return;
    if (selectedIds.length === 0 || !bulkAction) return;
    if (bulkAction === 'reprovar' && bulkMotivo.trim() === '') return;

    isSubmittingBulkRef.current = true;
    setIsSubmittingBulk(true);

    try {
      if (bulkAction === 'aprovar') {
        await props.AprovarResgateBulk(selectedIds, 2, bulkMotivo);
      } else {
        await props.ReprovarResgateBulk(selectedIds, bulkMotivo);
      }

      setIsBulkModalOpen(false);
      setBulkAction(null);
      setBulkMotivo('');
      setSelectedIds([]);

      props.ResetSolicitadosByCampanha(request, props.take);
      props.GetSolicitadosByCampanha([props.campanhaId], request, t);
    } finally {
      isSubmittingBulkRef.current = false;
      setIsSubmittingBulk(false);
    }
  }



  return (
    <Box sx={{
      '& @keyframes fadeIn': {
        '0%': {
          opacity: 0,
          transform: 'translateY(10px)'
        },
        '100%': {
          opacity: 1,
          transform: 'translateY(0)'
        }
      }
    }}>
      {/* Action Bar: SearchBar - Filtros - PagarPedido */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}>
        {/* Search Bar */}
        <TextField
          label="Pesquisar por usuário"
          placeholder="Digite o nome do usuário"
          value={searchValue}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{
            minWidth: 300,
            flex: 1,
            maxWidth: 400,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            }
          }}
          InputProps={{
            startAdornment: (
              <Icon
                icon="mdi:magnify"
                style={{
                  marginRight: 8,
                  color: '#9CA3AF',
                  width: 20,
                  height: 20
                }}
              />
            ),
          }}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Badge
            badgeContent={activeFilterCount}
            color="primary"
            invisible={activeFilterCount === 0}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.7rem',
                height: 20,
                minWidth: 20,
                borderRadius: '50%',
              }
            }}
          >
            <Button
              size="md"
              variant="secondary"
              iconName="mdi:filter-variant"
              onClick={handleOpenDrawer}
            >
              Filtros
            </Button>
          </Badge>

          {isAdministradorCarteira && (
            <>
              <Button
                size="md"
                variant="primary"
                iconName="mdi:file-document-multiple"
                onClick={handleNavigateToPagarPedido}
              >
                Gerenciar Lotes
              </Button>
              <Button
                size="md"
                variant="success"
                iconName="mdi:plus-circle"
                onClick={handleNavigateToGerarLote}
              >
                Gerar Lote
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <Box
          sx={{
            mb: 2,
            py: 1,
            px: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Typography variant="body2" sx={{ color: 'grey.700' }}>
            {selectedIds.length} {selectedIds.length === 1 ? 'selecionado' : 'selecionados'}
            <Typography
              component="span"
              variant="body2"
              onClick={handleClearSelection}
              sx={{
                ml: 1.5,
                color: 'grey.500',
                cursor: 'pointer',
                '&:hover': { color: 'grey.800', textDecoration: 'underline' },
              }}
            >
              limpar
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              size="sm"
              variant="light"
              onClick={() => handleOpenBulkModal('reprovar')}
              disabled={isBulkLoading || isSubmittingBulk}
            >
              Rejeitar
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => handleOpenBulkModal('aprovar')}
              disabled={isBulkLoading || isSubmittingBulk}
            >
              Aprovar
            </Button>
          </Box>
        </Box>
      )}

      {/* Status Tags */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {statusOptions.map(function renderStatusChip(status) {
          const isSelected = statusFilter.includes(status.id);
          const chipColor = getStatusChipColor(Number(status.id));

          return (
            <Chip
              key={status.id}
              label={status.nome}
              onClick={() => handleStatusChipToggle(status.id)}
              variant={isSelected ? 'filled' : 'outlined'}
              color={chipColor}
              size="small"
              sx={{
                fontWeight: isSelected ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }
              }}
            />
          );
        })}
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell padding="checkbox" sx={{ bgcolor: 'grey.50' }}>
                <Checkbox
                  indeterminate={somePageSelected}
                  checked={allPageSelected}
                  onChange={handleToggleAllPage}
                  disabled={selectableIds.length === 0}
                  size="small"
                />
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('data')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Data
                  {renderSortIcon('data')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('usuario')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Usuário
                  {renderSortIcon('usuario')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('descricao')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Descrição
                  {renderSortIcon('descricao')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('id')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  ID
                  {renderSortIcon('id')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('tipo')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Tipo
                  {renderSortIcon('tipo')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('chavepix')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Chave PIX
                  {renderSortIcon('chavepix')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('status')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Status
                  {renderSortIcon('status')}
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('valor')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  Valor
                  {renderSortIcon('valor')}
                </Box>
              </TableCell>
              <TableCell width={50} sx={{ fontWeight: 600, color: 'grey.700' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 8, border: 'none' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography variant="body2" color="text.secondary">
                      Carregando dados...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4, border: 'none' }}>
                  <NoData />
                </TableCell>
              </TableRow>
            ) : (
              data.map((item) => {
                function handleModalClick() {
                  handleOpenModal(item);
                }

                const canSelect = item.CampanhaPedidoResgateStatusID === 1;
                const isSelected = selectedIds.includes(item.CampanhaPedidoResgateID);

                function handleRowCheckbox(e: React.ChangeEvent<HTMLInputElement>) {
                  e.stopPropagation();
                  handleToggleRow(item.CampanhaPedidoResgateID);
                }

                return (
                  <TableRow
                    key={item.CampanhaPedidoResgateID}
                    hover
                    selected={isSelected}
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                        cursor: 'pointer',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      },
                      '&:nth-of-type(even)': {
                        bgcolor: 'grey.25'
                      },
                      transition: 'all 0.2s ease-in-out',
                      animation: 'fadeIn 0.3s ease-in-out'
                    }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        disabled={!canSelect}
                        onChange={handleRowCheckbox}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                          Data do pedido
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.900' }}>
                          {moment(item.HoraPedido).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={item._Usuario.ImagemUrl}
                          alt={item._Usuario.NomeCompleto}
                          sx={{
                            width: 40,
                            height: 40,
                            border: '2px solid',
                            borderColor: 'grey.200'
                          }}
                        >
                          {item._Usuario.NomeCompleto?.substring(0, 2) || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.900' }}>
                            {item._Usuario.NomeCompleto}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'grey.600' }}>
                            {item.Premiacao.Campanha.Titulo}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.900' }}>
                          {item.Premiacao.Descricao}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                          Prêmio solicitado
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          color: theme.colors.primary,
                          fontWeight: 600
                        }}
                      >
                        #{item.CampanhaPedidoResgateID}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={item.Premiacao.PremiacaoTipo.Titulo}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          color: theme.colors.primary,
                          borderColor: theme.colors.primary,
                          backgroundColor: 'info.50'
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            color: 'grey.800',
                            fontWeight: 500,
                            fontSize: '0.875rem'
                          }}
                        >
                          {item._Usuario.ChavePix || 'Não informado'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                          Chave PIX
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      {item.CampanhaPedidoResgateStatus && (
                        <Tag
                          full
                          variant={getStatusVariant(item.CampanhaPedidoResgateStatusID)}
                        >
                          {item.CampanhaPedidoResgateStatus.Titulo}
                        </Tag>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: 'success.main',
                            fontWeight: 700,
                            fontSize: '1.1rem'
                          }}
                        >
                          {formatToBRL(Number(item.ValorOriginal))}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                          Valor do Pedido
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={handleModalClick}
                        sx={{
                          color: theme.colors.primary,
                          backgroundColor: 'primary.50',
                          borderRadius: 2,
                          '&:hover': {
                            color: 'primary.dark',
                            backgroundColor: 'primary.100',
                            transform: 'scale(1.05)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <Icon icon='mdi:dots-vertical' width={22} height={22} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box sx={{ backgroundColor: 'grey.25', borderTop: '1px solid', borderColor: 'grey.200' }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage}
            onPageChange={handlePageChange}
            rowsPerPage={request.take}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={formatPaginationLabel}
            sx={{
              '& .MuiTablePagination-toolbar': {
                paddingX: 3,
                paddingY: 2
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: 'grey.700',
                fontWeight: 500
              },
              '& .MuiTablePagination-actions button': {
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.50'
                }
              }
            }}
          />
        </Box>
      </TableContainer>

      {/* Modal */}
      <Modal open={isModalOpen} onOpenChange={setIsModalOpen}>
        <ModalContent >
          {selectedItem && (
            <AprovarPedidoDetalhes
              campanhaPedidoResgate={selectedItem}
              onClose={handleCloseModal}
            />
          )}
        </ModalContent>
      </Modal>

      {/* Bulk Confirmation Modal */}
      <Modal open={isBulkModalOpen} onOpenChange={setIsBulkModalOpen}>
        <ModalContent>
          <Box sx={{ p: 3, minWidth: 380, maxWidth: 440 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
              {bulkAction === 'aprovar' ? 'Aprovar pedidos' : 'Rejeitar pedidos'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'grey.600', mb: 2.5 }}>
              {selectedIds.length} {selectedIds.length === 1 ? 'pedido selecionado' : 'pedidos selecionados'}
            </Typography>

            <TextField
              fullWidth
              multiline
              minRows={3}
              value={bulkMotivo}
              onChange={(e) => setBulkMotivo(e.target.value)}
              placeholder={bulkAction === 'reprovar' ? 'Motivo da rejeição' : 'Observação (opcional)'}
              disabled={isBulkLoading || isSubmittingBulk}
              size="small"
              sx={{ mb: 2.5 }}
            />

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="light" size="sm" onClick={handleCloseBulkModal} disabled={isBulkLoading || isSubmittingBulk}>
                Cancelar
              </Button>
              <Button
                variant={bulkAction === 'aprovar' ? 'primary' : 'danger'}
                size="sm"
                onClick={handleConfirmBulk}
                disabled={isBulkLoading || isSubmittingBulk || (bulkAction === 'reprovar' && bulkMotivo.trim() === '')}
              >
                {isBulkLoading || isSubmittingBulk ? 'Processando…' : 'Confirmar'}
              </Button>
            </Box>

            <StateMensage stateLoadingNames={[CAMPANHA_RESGATE_APROVAR_BULK, CAMPANHA_RESGATE_REPROVAR_BULK]} stateErrorNames={[CAMPANHA_RESGATE_APROVAR_BULK, CAMPANHA_RESGATE_REPROVAR_BULK]} />
          </Box>
        </ModalContent>
      </Modal>

      {/* Filters Drawer */}
      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        premiacaoTipoFilter={premiacaoTipoFilter}
        onPremiacaoTipoFilterChange={handlePremiacaoTipoFilterChange}
        dateStart={dateStart}
        dateEnd={dateEnd}
        onDateStartChange={handleDateStartChange}
        onDateEndChange={handleDateEndChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        statusOptions={statusOptions}
        premiacaoTipoOptions={premiacaoTipoOptions}
      />
    </Box>
  );
}
export default connector(PagedResgates);